import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { useResetPasswordExperience } from '@aqx-core/common/services/reset-password-experience';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

import AuthCompletedContent from '../../../shared/completed-content';
import ResetPasswordAccountDetails from './details';

const ResetPasswordComplete: FCC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyProfile = useCompanyProfile();
  const { hasMT4DemoLoginConfig, hasMT5DemoLoginConfig } = useMemo(() => {
    return validateCompanyAccess(companyProfile);
  }, [companyProfile]);

  const { details } = useResetPasswordExperience();

  const { login, password } = details || {};

  const onClickLogInButton = useCallback(() => {
    const demoLoginType = authenticationViewController.getDemoLoginTypeFromCompanyAccess({
      hasMT4DemoLoginConfig,
      hasMT5DemoLoginConfig
    });

    if (demoLoginType && login && password) {
      authenticationViewController.setPrefilledValues(login, password, demoLoginType);
    }

    navigate(AUTHENTICATION_ROUTES.LOGIN);
  }, [hasMT4DemoLoginConfig, hasMT5DemoLoginConfig, login, navigate, password]);

  return (
    <AuthCompletedContent
      header={t('We’ve reset your password. Your demo account is ready.')}
      description={t('Here are the login and account details. We’ve also sent these to your email.')}
      extraContent={<ResetPasswordAccountDetails />}
      onClickPrimaryButton={onClickLogInButton}
      primaryButtonText={t('Back to Log In')}
      primaryButtonDataTestId={EDataTestId.RESET_PASSWORD_BACK_TO_LOG_IN}
    />
  );
};

export default ResetPasswordComplete;
